import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, MessageCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { API } from "@/lib/contact";

const STATUSES = ["new", "contacted", "quoted", "closed"];
const STATUS_COLORS = {
  new: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  contacted: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  quoted: "text-violet-400 border-violet-500/40 bg-violet-500/10",
  closed: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
};

const inputCls =
  "w-full bg-[#131B2A] border border-zinc-700 focus:border-amber-500 outline-none text-white placeholder:text-zinc-600 px-4 py-3.5 text-sm transition-colors duration-300";

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const waLead = (lead) => {
  const digits = (lead.phone || "").replace(/\D/g, "");
  const num = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${num}?text=${encodeURIComponent(
    `Hello ${lead.name}, this is Bhargav from Rorix regarding your inquiry for ${lead.product}.`
  )}`;
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("rorix_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchLeads = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("rorix_admin_token");
        setToken(null);
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("rorix_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("rorix_admin_token");
    setToken(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0F17] bg-blueprint flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md border border-zinc-800 bg-[#0D1220] p-10"
        >
          <div className="flex items-center gap-2.5 mb-2">
            <span className="w-3 h-3 bg-amber-500" />
            <span className="font-display text-3xl font-bold tracking-[0.15em] text-white">RORIX</span>
          </div>
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-8">Admin Console · Restricted</p>
          <form onSubmit={login} data-testid="admin-login-form">
            <input type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
            <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-4`} data-testid="admin-password-input" />
            {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
            <button type="submit" className="clip-notch mt-6 w-full bg-amber-500 text-[#0B0F17] font-display font-bold tracking-[0.2em] uppercase px-6 py-3.5 hover:bg-amber-400 transition-colors duration-300" data-testid="admin-login-button">
              Enter Console
            </button>
          </form>
          <Link to="/" className="block mt-6 font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-600 hover:text-amber-400 transition-colors" data-testid="admin-back-link">
            ← Back to site
          </Link>
        </motion.div>
      </div>
    );
  }

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white" data-testid="admin-dashboard">
      <header className="border-b border-zinc-800 bg-[#0D1220]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 bg-amber-500" />
            <span className="font-display text-xl font-bold tracking-[0.15em]">RORIX</span>
            <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-0.5">Lead Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchLeads(token)} className="p-2 border border-zinc-700 text-zinc-400 hover:text-amber-400 hover:border-amber-500/50 transition-colors" data-testid="admin-refresh-button" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2 border border-zinc-700 font-mono2 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-red-400 hover:border-red-500/50 transition-colors" data-testid="admin-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
          {[{ label: "Total", value: leads.length, key: "all" }, ...STATUSES.map((s) => ({ label: s, value: counts[s], key: s }))].map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`admin-filter-${c.key}`}
              className={`text-left border p-5 transition-colors duration-300 ${filter === c.key ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 bg-[#0D1220] hover:border-zinc-600"}`}
            >
              <div className="font-display text-3xl font-bold text-white">{c.value}</div>
              <div className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-1">{c.label}</div>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="border border-zinc-800 bg-[#0D1220] p-16 text-center" data-testid="admin-empty-state">
            <ShieldCheck className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <p className="font-mono2 text-xs tracking-[0.25em] uppercase text-zinc-500">No inquiries in this view yet</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-lead-list">
            {filtered.map((lead, i) => (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="border border-zinc-800 bg-[#0D1220] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                data-testid={`admin-lead-row-${lead.id.slice(0, 8)}`}
              >
                <div className="md:col-span-3">
                  <p className="font-display text-lg font-bold uppercase tracking-wide">{lead.name}</p>
                  <p className="text-xs text-zinc-500">{lead.company}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="font-mono2 text-xs text-amber-400 uppercase tracking-wider">{lead.product}</p>
                  {lead.quantity && <p className="text-xs text-zinc-500 mt-0.5">Qty: {lead.quantity}</p>}
                </div>
                <div className="md:col-span-3">
                  <a href={`tel:+91${(lead.phone || "").replace(/\D/g, "").slice(-10)}`} className="text-sm text-zinc-300 hover:text-amber-400 transition-colors" data-testid={`admin-lead-phone-${lead.id.slice(0, 8)}`}>
                    {lead.phone}
                  </a>
                  {lead.message && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{lead.message}</p>}
                </div>
                <div className="md:col-span-2">
                  <select
                    value={lead.status}
                    onChange={(e) => setStatus(lead.id, e.target.value)}
                    className={`font-mono2 text-[10px] tracking-[0.2em] uppercase border px-3 py-2 bg-transparent outline-none cursor-pointer ${STATUS_COLORS[lead.status]}`}
                    data-testid={`admin-lead-status-${lead.id.slice(0, 8)}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0D1220] text-white">{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 flex md:justify-end">
                  <a
                    href={waLead(lead)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 border border-emerald-500/50 text-emerald-400 font-mono2 text-[10px] tracking-[0.2em] uppercase px-4 py-2 hover:bg-emerald-500/10 transition-colors"
                    data-testid={`admin-lead-whatsapp-${lead.id.slice(0, 8)}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
